import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  TextInput,
  PasswordInput,
  Button,
  Paper,
  Title,
  Container,
  Stack,
  Alert,
  Text,
  Loader,
  Center,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { invitationsApi } from '@/services/invitations.api';
import { useAuth } from '@/features/auth/useAuth';

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'valid' | 'invalid'>('loading');
  const [email, setEmail] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [token, setToken] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');

    if (!tokenParam) {
      setStatus('invalid');
      return;
    }

    setToken(tokenParam);

    const loadInvitation = async () => {
      try {
        const data = await invitationsApi.getByToken(tokenParam);
        if (!data.valid) {
          setStatus('invalid');
          return;
        }
        setEmail(data.email);
        setTenantName(data.tenantName);
        setStatus('valid');
      } catch {
        setStatus('invalid');
      }
    };

    loadInvitation();
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid invitation link');
      return;
    }

    setSubmitting(true);

    try {
      await invitationsApi.accept(token, name, password);
      checkAuth();
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <Container size={420} my={40}>
        <Title ta="center" mb="xl">
          Accept Invitation
        </Title>

        <Paper withBorder shadow="md" p={30} radius="md">
          <Center>
            <Stack align="center">
              <Loader size="lg" />
              <span>Loading invitation...</span>
            </Stack>
          </Center>
        </Paper>
      </Container>
    );
  }

  if (status === 'invalid') {
    return (
      <Container size={420} my={40}>
        <Title ta="center" mb="xl">
          Accept Invitation
        </Title>

        <Paper withBorder shadow="md" p={30} radius="md">
          <Stack>
            <Alert icon={<IconAlertCircle size={16} />} title="Invalid invitation" color="red">
              This invitation is invalid or has expired.
            </Alert>
            <Button variant="light" fullWidth component={Link} to="/login">
              Go to Login
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size={420} my={40}>
      <Title ta="center" mb="xl">
        Join {tenantName}
      </Title>

      <Paper withBorder shadow="md" p={30} radius="md">
        <form onSubmit={handleSubmit}>
          <Stack>
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
                {error}
              </Alert>
            )}

            <Text size="sm" c="dimmed">
              Complete your account to accept the invitation.
            </Text>

            <TextInput label="Email" value={email} readOnly disabled />

            <TextInput
              label="Name"
              placeholder="Your name"
              required
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />

            <PasswordInput
              label="Password"
              placeholder="At least 8 characters"
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />

            <PasswordInput
              label="Confirm Password"
              placeholder="Re-enter your password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.currentTarget.value)}
            />

            <Button type="submit" fullWidth loading={submitting}>
              Accept Invitation
            </Button>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}
